import { z } from 'zod';
// ============================================================================
// Recurrence rule (loosely modeled on RFC 5545 RRULE; simpler subset)
// ============================================================================
export const RecurrenceFrequencySchema = z.enum([
    'daily',
    'weekly',
    'monthly',
    'yearly',
    'weekdays',
]);
export const WeekdaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export const RecurrenceRuleSchema = z
    .object({
    frequency: RecurrenceFrequencySchema,
    interval: z.number().int().min(1).default(1),
    /** Required when frequency='weekdays'. */
    weekdays: z.array(WeekdaySchema).optional(),
    /** Total occurrence count. Mutually exclusive with `until`. */
    count: z.number().int().min(1).nullable().optional(),
    /** Stop after this date/time. Mutually exclusive with `count`. */
    until: z.string().datetime().nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.frequency === 'weekdays' && (!v.weekdays || v.weekdays.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['weekdays'],
            message: 'weekdays is required when frequency=weekdays',
        });
    }
    if (v.count != null && v.until != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'count and until are mutually exclusive',
        });
    }
});
// ============================================================================
// Event reminders
// ============================================================================
export const ReminderChannelSchema = z.enum(['voice', 'sms', 'email']);
/**
 * `offset` is either an ISO 8601 duration ("PT24H", "P1W") meaning "X before
 * event start", or an ISO 8601 datetime ("2026-06-01T08:00:00Z") for a fixed
 * absolute reminder time. We don't strictly distinguish the two in the schema
 * — the consumer parses based on whether the string starts with `P`.
 */
const DURATION_RE = /^P(?:\d+Y)?(?:\d+M)?(?:\d+W)?(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?(?:\d+S)?)?$/;
const ISO_DT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
export const EventReminderSchema = z.object({
    channels: z.array(ReminderChannelSchema).min(1),
    offset: z
        .string()
        .min(1)
        .refine((s) => DURATION_RE.test(s) || ISO_DT_RE.test(s), {
        message: 'offset must be an ISO 8601 duration (e.g., PT24H) or datetime',
    }),
});
// ============================================================================
// Event entity
// ============================================================================
export const EventStatusSchema = z.enum([
    'pending',
    'awaiting',
    'confirmed',
    'failed',
    // Phase 3c.1 — public booking flow.
    'pending_approval', // visitor-submitted, awaiting admin approval (scheduler ignores)
    'scheduled', // reserved for forward-compat (not yet emitted by any code path)
    // Task #6 — inbound Google Calendar deletions mark the xsc row cancelled
    // instead of hard-deleting so audit / history remain intact. Scheduler
    // skips dispatch on cancelled rows.
    'cancelled',
]);
const ChannelDeliveryStatusSchema = z.enum([
    'na',
    'pending',
    'sent',
    'completed',
    'failed',
    'verified',
    'caller_disconnect',
]);
export const EventDeliveryStatusSchema = z.object({
    voice: ChannelDeliveryStatusSchema.optional(),
    sms: ChannelDeliveryStatusSchema.optional(),
    email: ChannelDeliveryStatusSchema.optional(),
    identity_verify: ChannelDeliveryStatusSchema.optional(),
});
/**
 * Per-channel dispatch attempt counters. Used by the scheduler to bound
 * retries on `failed` channels (otherwise a single transient failure
 * marks a reminder forever-dead — see migration 0023). The count is
 * incremented on every dispatch attempt (success or failure); the
 * scheduler treats `delivery_status[channel] === 'failed'` as eligible
 * for retry only while attempts < MAX_DELIVERY_ATTEMPTS.
 */
export const EventDeliveryAttemptsSchema = z.object({
    voice: z.number().int().min(0).optional(),
    sms: z.number().int().min(0).optional(),
    email: z.number().int().min(0).optional(),
    identity_verify: z.number().int().min(0).optional(),
});
export const EventSchema = z
    .object({
    id: z.string().regex(/^evt_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    contact_profile_id: z.string().regex(/^cp_[A-Za-z0-9_-]+$/).nullable().optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).nullable().optional(),
    attendee_name: z.string().min(1).max(120).optional(),
    attendee_email: z.string().email().optional(),
    /** E.164 format. */
    attendee_phone: z
        .string()
        .min(1)
        .max(32)
        .refine((v) => {
        // Phase 2.7 — accept E.164 NANP (`+1XXXXXXXXXX`) only, plus
        // already-normalized 11-digit `1XXXXXXXXXX` for tolerance.
        // The web form normalizes 10-digit input to `+1` before submit.
        const digits = v.replace(/\D+/g, '');
        if (digits.length === 10)
            return true;
        if (digits.length === 11 && digits.startsWith('1'))
            return true;
        return false;
    }, {
        message: 'attendee_phone must be a 10- or 11-digit NANP number (e.g. 5551234567 or +15551234567)',
    }),
    title: z.string().min(1).max(500),
    start_at: z.string().datetime(),
    end_at: z.string().datetime(),
    recurrence: RecurrenceRuleSchema.nullable().optional(),
    reminders: z.array(EventReminderSchema).default([]),
    status: EventStatusSchema.default('pending'),
    delivery_status: EventDeliveryStatusSchema.default({}),
    delivery_attempts: EventDeliveryAttemptsSchema.optional(),
    parent_event_id: z.string().regex(/^evt_[A-Za-z0-9_-]+$/).nullable().optional(),
    dnc: z.boolean().default(false),
    /** Phase 3c.1 — present when the event was created via public booking. */
    service_profile_id: z.string().regex(/^sp_[A-Za-z0-9_-]+$/).nullable().optional(),
    /** 'admin' is the default for events created via legacy admin/API paths. */
    booking_source: z.enum(['public', 'admin', 'api']).optional(),
    external_booker_email: z.string().email().nullable().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
})
    .refine((e) => e.end_at >= e.start_at, {
    path: ['end_at'],
    message: 'end_at must be on or after start_at',
});
// ============================================================================
// Create / update payloads
// ============================================================================
export const EventCreateSchema = z
    .object({
    contact_profile_id: z.string().regex(/^cp_[A-Za-z0-9_-]+$/).optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).optional(),
    attendee_name: z.string().min(1).max(120).optional(),
    attendee_email: z.string().email().optional(),
    attendee_phone: z
        .string()
        .min(1)
        .max(32)
        .refine((v) => {
        // Phase 2.7 — accept E.164 NANP (`+1XXXXXXXXXX`) only, plus
        // already-normalized 11-digit `1XXXXXXXXXX` for tolerance.
        // The web form normalizes 10-digit input to `+1` before submit.
        const digits = v.replace(/\D+/g, '');
        if (digits.length === 10)
            return true;
        if (digits.length === 11 && digits.startsWith('1'))
            return true;
        return false;
    }, {
        message: 'attendee_phone must be a 10- or 11-digit NANP number (e.g. 5551234567 or +15551234567)',
    }),
    title: z.string().min(1).max(500),
    start_at: z.string().datetime(),
    end_at: z.string().datetime(),
    recurrence: RecurrenceRuleSchema.optional(),
    reminders: z.array(EventReminderSchema).default([]),
})
    .refine((e) => e.end_at >= e.start_at, {
    path: ['end_at'],
    message: 'end_at must be on or after start_at',
});
export const EventUpdateSchema = z
    .object({
    title: z.string().min(1).max(500).optional(),
    start_at: z.string().datetime().optional(),
    end_at: z.string().datetime().optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).optional(),
    attendee_name: z.string().min(1).max(120).optional(),
    attendee_email: z.string().email().optional(),
    attendee_phone: z
        .string()
        .min(1)
        .max(32)
        .refine((v) => {
        const digits = v.replace(/\D+/g, '');
        return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }, {
        message: 'attendee_phone must be a 10- or 11-digit NANP number (e.g. 5551234567 or +15551234567)',
    })
        .optional(),
    status: z.enum(['confirmed', 'awaiting']).optional(),
    reminders: z.array(EventReminderSchema).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' })
    .refine((v) => !(v.start_at && v.end_at) || v.end_at >= v.start_at, {
    path: ['end_at'],
    message: 'end_at must be on or after start_at',
});
//# sourceMappingURL=events.js.map